import React, { useLayoutEffect, useRef, useEffect, useMemo, useState } from 'react';
import './OpportunitiesChart.scss';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import * as am5core from '@amcharts/amcharts5';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import EmptyState from '../../../components/empty-state/EmptyState';
import { useAppSelector, useAppDispatch } from '../../../hooks/storeHooks';
import { exportChart } from '../../../utilities/ExportChart';
import DownloadChart from '../../../components/time-series-chart/download/downloadChart';
import Loader from '../../common/Loader/Loader';
import { TabContent } from '../Recommendations';
import UIButtonToolbar from '../../navigation/components/UIButtonToolbar';
import { prepareChartContainer, customizeAxisLabels, createTooltip } from '../../../utilities/chartHelpers';
import { setChartActiveTab } from '../RecommendationsSlice';
import { createFilterFunction, RELEVANT_FILTER_SECTIONS } from '../../../utilities/GlobalFilterUtil';
import FullScreenIcon from '../../common/FullScreenIcon/FullScreenIcon';

interface WellData {
  x: number;
  y: number;
  name: string;
  color: string;
  wellId: number;
}

interface OpportunitiesData {
  MaxControl: number[][];
  MinControl: number[][];
  Wells: WellData[];
}

interface OpportunitiesChartProps {
  header: string;
  parentActiveTab?: string;
  showTabs?: boolean;
  data?: OpportunitiesData;
  fullScreen?: boolean;
}

const OpportunitiesChart: React.FC<OpportunitiesChartProps> = ({
  header,
  parentActiveTab = 'esp',
  showTabs = true,
  data,
  fullScreen = false,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLElement>(null);

  const [isFullscreen, setIsFullscreen] = useState(false);

  const dispatch = useAppDispatch();
  const recommendationsOpportunities = useAppSelector(
    (state: any) => state.Recommendations?.opportunities?.data,
  ) as OpportunitiesData;
  const isLoading = useAppSelector((state: any) => state.Recommendations?.opportunities?.loading);
  const activeTab = useAppSelector((state: any) => state.Recommendations?.chartActiveTab);
  const { assets } = useAppSelector((state) => state.assetGroups);

  const opportunitiesData = data ? data : recommendationsOpportunities;

  // Filter-related selectors
  const selectedFilters = useAppSelector((state) => state.WellFiltersSlice.selectedFilters);
  const filteredAssetList = useAppSelector((state) => state.WellFiltersSlice.filteredAssetList);

  // Apply filter if active filters exist
  const hasActiveFilters = useMemo(() => {
    return selectedFilters.some((filter) => RELEVANT_FILTER_SECTIONS.includes(filter.section as any));
  }, [selectedFilters]);

  const filterDataByAssetList = useMemo(() => {
    return createFilterFunction(hasActiveFilters, filteredAssetList, 'name');
  }, [hasActiveFilters, filteredAssetList]);

  const ChartTabs: TabContent[] = useMemo(() => {
    const tabs = [
      {
        id: 0,
        name: 'Optimization opportunities',
        active: activeTab === 'ESP',
      },
    ];

    if (parentActiveTab === 'esp') {
      tabs.push({
        id: 1,
        name: 'Pwf/Hz chart',
        active: activeTab === 'PwfHz',
      });
    }

    return tabs;
  }, [activeTab, parentActiveTab]);

  useEffect(() => {
    if (parentActiveTab === 'gl' && activeTab === 'PwfHz') {
      dispatch(setChartActiveTab('ESP'));
    }
  }, [dispatch, parentActiveTab, activeTab]);

  const handleTabChange = (tabId: number) => {
    const tab = ChartTabs.find((button) => button.id === tabId);
    if (tab) {
      if (tab.name === 'Optimization opportunities') {
        dispatch(setChartActiveTab('ESP'));
      } else if (tab.name === 'Pwf/Hz chart') {
        dispatch(setChartActiveTab('PwfHz'));
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const handleChartDownload = async (option: string) => {
    const element = document.getElementById(`opportunities-chart-content`);
    let filename = 'OptimizationOpportunitiesChart';

    const activeTabName = ChartTabs.find((tab) => tab.active)?.name;
    if (activeTabName === 'Pwf/Hz chart') {
      filename = 'PwfHzChart';
    }
    if (!element) return;
    await exportChart(element, getExportType(option), filename, undefined, undefined, 'Wells Opportunities');
  };

  const getExportType = (option: string): 'JPG' | 'PDF' => (option === 'Download as PDF' ? 'PDF' : 'JPG');

  const chartData = useMemo(() => {
    if (!opportunitiesData?.Wells) return [];

    const wellsData = opportunitiesData.Wells.map((well) => ({
      x: well.x,
      y: well.y,
      name: well.name,
      color: well.color,
      wellId: well.wellId,
    }));

    return filterDataByAssetList(wellsData);
  }, [opportunitiesData, filterDataByAssetList]);

  const controlLinesData = useMemo(() => {
    if (!opportunitiesData) return { maxControl: [], minControl: [] };
    return {
      maxControl: opportunitiesData.MaxControl?.map(([x, y]) => ({ x, y })) || [],
      minControl: opportunitiesData.MinControl?.map(([x, y]) => ({ x, y })) || [],
    };
  }, [opportunitiesData]);

  useLayoutEffect(() => {
    if (!chartRef.current) return;

    // Check if there is data to display (wells or control lines)
    const hasAnyData =
      chartData.length > 0 || controlLinesData.minControl.length > 0 || controlLinesData.maxControl.length > 0;

    if (!hasAnyData) return;
    if (!prepareChartContainer(chartRef.current)) return;

    const root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Dark.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        layout: root.verticalLayout,
        paddingLeft: 10,
        paddingRight: 30,
        paddingTop: 20,
        paddingBottom: 10,
        background: am5.Rectangle.new(root, {
          fill: am5.color('#122031'),
          fillOpacity: 0,
        }),
      }),
    );

    const xAxisRenderer = am5xy.AxisRendererX.new(root, {
      minGridDistance: 50,
      strokeWidth: 1,
      strokeOpacity: 1,
      stroke: am5core.color('#4A5463'),
    });

    const xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, { renderer: xAxisRenderer }));
    customizeAxisLabels(xAxisRenderer, 12);

    const yAxisRenderer = am5xy.AxisRendererY.new(root, {
      strokeWidth: 1,
      strokeOpacity: 1,
      stroke: am5core.color('#4A5463'),
    });

    const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, { renderer: yAxisRenderer }));
    customizeAxisLabels(yAxisRenderer, 12);

    // Dynamic axis labels based on active tab
    const xAxisLabel = parentActiveTab === 'gl' ? 'Injection rate (mscf/d)' : 'Frequency (Hz)';
    const yAxisLabel = activeTab === 'PwfHz' || parentActiveTab === 'gl' ? 'Pwf (psig)' : 'Pump intake pressure (psig)';

    xAxis.children.moveValue(
      am5.Label.new(root, {
        text: xAxisLabel,
        x: am5.percent(50),
        centerX: am5.percent(50),
        fontFamily: 'Mulish',
        fontSize: 12,
        fontWeight: '400',
        marginTop: 10,
      }),
      xAxis.children.length - 1,
    );

    yAxis.children.unshift(
      am5.Label.new(root, {
        rotation: -90,
        text: yAxisLabel,
        y: am5.percent(50),
        centerX: am5.percent(50),
        textAlign: 'center',
        fontFamily: 'Mulish',
        fontSize: 12,
        fontWeight: '400',
        marginRight: -5,
      }),
    );

    const gridSettings = {
      stroke: am5.color('#616a77'),
      strokeWidth: 1,
      strokeOpacity: 0.3,
      visible: true,
      strokeDasharray: [2, 2],
    };

    yAxis.get('renderer').grid.template.setAll(gridSettings);
    xAxis.get('renderer').grid.template.setAll(gridSettings);

    // --- SERIES ---
    let minControlSeries: am5xy.LineSeries | undefined, maxControlSeries: am5xy.LineSeries | undefined;

    if (controlLinesData.minControl.length > 0) {
      let minControlName = 'Min PIP';
      if (activeTab === 'PwfHz' || parentActiveTab === 'gl') {
        minControlName = 'Min pwf';
      }

      minControlSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: minControlName,
          xAxis,
          yAxis,
          valueYField: 'y',
          valueXField: 'x',
          stroke: am5.color('#F97066'),
        }),
      );
      if (!showTabs) {
        minControlSeries.strokes.template.setAll({ strokeDasharray: [4, 4] });
      }
      minControlSeries.data.setAll(controlLinesData.minControl);
    }

    if (controlLinesData.maxControl.length > 0) {
      let maxControlName = 'Max HZ';
      if (parentActiveTab === 'gl') {
        maxControlName = 'Max injection rate';
      }

      maxControlSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: maxControlName,
          xAxis,
          yAxis,
          valueYField: 'y',
          valueXField: 'x',
          stroke: am5.color('#60BFDA'),
        }),
      );
      if (!showTabs) {
        maxControlSeries.strokes.template.setAll({ strokeDasharray: [4, 4] });
      }
      maxControlSeries.data.setAll(controlLinesData.maxControl);
    }

    const wellsSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Wells',
        xAxis,
        yAxis,
        valueYField: 'y',
        valueXField: 'x',
        stroke: showTabs ? am5.color('#E7F2FF') : am5.color('#006DFA'),
        fill: showTabs ? am5.color('#E7F2FF') : am5.color('#006DFA'),
      }),
    );

    wellsSeries.strokes.template.setAll({ strokeOpacity: 0 });
    wellsSeries.adapters.add('legendLabelText', () => 'Wells');

    wellsSeries.bullets.push((root, _series, dataItem) => {
      const data = dataItem.dataContext as any;
      const wellColor = data.color || '#60BFDA';

      // Create a container for the bullet components
      const container = am5.Container.new(root, {
        centerX: am5.p50,
        centerY: am5.p50,
        width: 20, // Make container large enough for hover effect
        height: 20,
      });

      // Create hover background circle (initially invisible)
      const hoverCircle = container.children.push(
        am5.Circle.new(root, {
          radius: 12,
          fill: am5.Color.lighten(am5.color(wellColor), 0.2),
          fillOpacity: 0.6,
          strokeOpacity: 0,
          centerX: am5.p50,
          centerY: am5.p50,
          // Initially hidden
          opacity: 0,
        }),
      );

      // Create the triangle marker
      const triangle = container.children.push(
        am5.Triangle.new(root, {
          fill: wellColor,
          stroke: wellColor,
          strokeWidth: 1,
          width: 10,
          height: 8,
          centerX: am5.p50,
          centerY: am5.p50,
          rotation: 0,
        }),
      );

      // Set up container hover interactions
      container.events.on('pointerover', function () {
        // Show the background circle on hover
        hoverCircle.animate({
          key: 'opacity',
          to: 1,
          duration: 200,
        });

        // Make triangle more prominent
        triangle.animate({
          key: 'scale',
          to: 1.2,
          duration: 200,
        });
      });

      container.events.on('pointerout', function () {
        // Hide background circle when not hovering
        hoverCircle.animate({
          key: 'opacity',
          to: 0,
          duration: 200,
        });

        // Return triangle to normal size
        triangle.animate({
          key: 'scale',
          to: 1,
          duration: 200,
        });
      });

      // Log bullet details on click
      container.events.on('click', function () {
        try {
          const well = assets?.find((asset) => asset.assetName?.trim() === data?.name?.trim());
          if (well && well.assetId) {
            const baseUrl = window.location.origin;
            window.open(`${baseUrl}/pumpchecker/ESPDiagnosis/${well?.assetId}`, '_blank');
          }
        } catch (_e) {
          console.log('Error navigating to diagnosis page');
        }
      });

      // Set cursor to pointer on hover
      container.set('cursorOverStyle', 'pointer');

      return am5.Bullet.new(root, { sprite: container });
    });

    const tooltip = createTooltip('{name}', root, {
      backgroundColor: '#001023',
      backgroundOpacity: 0.9,
      strokeColor: '#001023',
      textColor: '#60BFDA',
      fontSize: 12,
      fontFamily: 'Mulish',
    });

    // Use pointer-based positioning so the tail targets the data point (triangle center)
    tooltip.setAll({
      pointerOrientation: 'down',
      tooltipPosition: 'pointer',
      centerX: am5.percent(50),
      dy: -18,
      animationDuration: 200,
      paddingTop: 6,
      paddingBottom: 10, // Slightly more bottom padding for the pointer
      paddingLeft: 10,
      paddingRight: 10,
      dx: -10,
    });

    const tooltipBackground = tooltip.get('background');
    if (tooltipBackground) {
      tooltipBackground.setAll({
        fill: am5.color('#001023'),
        stroke: am5.color('#001023'),
        fillOpacity: 0.9,
        // cornerRadius: 4,
      });
    }

    wellsSeries.set('tooltip', tooltip);

    wellsSeries.data.setAll(chartData);

    const cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        behavior: 'zoomX',
        xAxis: xAxis,
      }),
    );
    cursor.lineX.setAll({ stroke: am5.color('#60BFDA'), strokeWidth: 1, strokeOpacity: 0.5 });
    cursor.lineY.setAll({ stroke: am5.color('#60BFDA'), strokeWidth: 1, strokeOpacity: 0.5 });

    // --- LEGEND ---
    const legend = chart.children.push(
      am5.Legend.new(root, {
        x: am5.p50,
        centerX: am5.p50,
        paddingLeft: 80,
        marginTop: 30,
        useDefaultMarker: true,
      }),
    );

    legend.labels.template.setAll({
      fontSize: 12,
      fontFamily: 'Mulish',
      fontWeight: '500',
    });

    legend.labels.template.adapters.add('text', (text, target) => {
      const legendData = target.dataItem?.dataContext as any;
      return legendData?.name ?? text;
    });

    legend.markers.template.setAll({ width: 10, height: 10 });
    legend.markerRectangles.template.setAll({
      cornerRadiusTL: 50,
      cornerRadiusTR: 50,
      cornerRadiusBL: 50,
      cornerRadiusBR: 50,
      strokeWidth: 2,
    });

    legend.markerRectangles.template.adapters.add('fill', (fill, target) => {
      const legendData = target.dataItem?.dataContext as any;
      return legendData?.color ?? fill;
    });

    legend.markerRectangles.template.adapters.add('stroke', (stroke, target) => {
      const legendData = target.dataItem?.dataContext as any;
      return legendData?.color ?? stroke;
    });

    legend.markerRectangles.template.adapters.add('fillOpacity', (_, target) => {
      const legendData = target.dataItem?.dataContext as any;
      return legendData?.visible === false ? 0 : 1;
    });

    legend.itemContainers.template.events.on('click', (ev) => {
      const legendData = ev.target.dataItem?.dataContext as any;
      if (!legendData) return;

      const isVisible = legendData.visible !== false;
      legendData.visible = !isVisible;

      // Find and toggle the corresponding series
      const allSeries = [minControlSeries, maxControlSeries, wellsSeries].filter(Boolean);
      allSeries.forEach((series) => {
        if (series && series.get('name') === legendData.name) {
          series.set('visible', !isVisible);
        }
      });

      const legendItem = legend.dataItems.find((item) => item.dataContext === legendData);
      if (legendItem) {
        const markerRectangle = legendItem.get('markerRectangle');
        if (markerRectangle) {
          markerRectangle.set('fillOpacity', !isVisible ? 1 : 0);
        }

        if (!showTabs && legendData.name === 'Wells') {
          const marker = legendItem.get('marker');
          if (marker && marker.children.length > 0) {
            const triangle = marker.children.getIndex(0) as am5.Triangle;
            if (triangle) {
              triangle.set('fillOpacity', !isVisible ? 1 : 0);
            }
          }
        }
      }
    });

    legend.itemContainers.template.set('cursorOverStyle', 'pointer');

    if (!showTabs) {
      legend.events.on('datavalidated', () => {
        legend.dataItems.forEach((dataItem) => {
          const legendData = dataItem.dataContext as any;
          const seriesName = legendData?.name;

          if (seriesName === 'Wells') {
            const marker = dataItem.get('marker');
            if (marker) {
              marker.children.clear();
              const triangle = am5.Triangle.new(root, {
                width: 10,
                height: 10,
                fill: am5.color('#006DFA'),
                stroke: am5.color('#006DFA'),
                strokeWidth: 2,
                centerX: am5.p50,
                centerY: am5.p50,
                x: 4,
                y: 6,
                rotation: 0,
              });

              triangle.adapters.add('fillOpacity', () => {
                return legendData?.visible === false ? 0 : 1;
              });

              marker.children.push(triangle);
            }
          }
        });
      });
    }

    const legendData = [
      minControlSeries && {
        name: minControlSeries.get('name'),
        color: am5.color('#F97066'),
        visible: true,
      },
      maxControlSeries && {
        name: maxControlSeries.get('name'),
        color: am5.color('#60BFDA'),
        visible: true,
      },
      wellsSeries && {
        name: 'Wells',
        color: showTabs ? am5.color('#E7F2FF') : am5.color('#006DFA'),
        visible: true,
      },
    ].filter(Boolean);

    // Set legend data
    legend.data.setAll(legendData);

    chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, [chartData, controlLinesData, activeTab, parentActiveTab, showTabs]);

  const hasData =
    (opportunitiesData?.Wells && opportunitiesData.Wells.length > 0) ||
    (opportunitiesData?.MinControl && opportunitiesData.MinControl.length > 0) ||
    (opportunitiesData?.MaxControl && opportunitiesData.MaxControl.length > 0);

  const chartContent = (
    <section
      ref={containerRef}
      className={`opportunities-chart-container ${isFullscreen ? 'fullscreen' : ''}`}
      style={{ margin: showTabs ? '' : '6px 8px' }}
    >
      <header className='opportunities-chart-header'>
        <div className='chart-title'>
          <h2>{header}</h2>
        </div>
        <div className='chart-actions'>
          {fullScreen && (
            <button
              analytics-id='opportunities-chart-fullscreen-btn'
              className='chart-action-button'
              onClick={toggleFullscreen}
            >
              <FullScreenIcon />
            </button>
          )}
          <button analytics-id='opportunities-chart-download-btn' className='chart-action-button'>
            <DownloadChart onClick={(option) => handleChartDownload(option)} canExportJpg={true} canExportPdf={true} />
          </button>
        </div>
      </header>

      <div className='chart-divider'></div>
      {showTabs && (
        <div className='opportunities-tabs'>
          <UIButtonToolbar uiButtons={ChartTabs} buttonClicked={handleTabChange} />
        </div>
      )}
      {isLoading ? (
        <Loader />
      ) : hasData ? (
        <div id='opportunities-chart-content' className='no-tabs'>
          <div ref={chartRef} className='chart-area' />
        </div>
      ) : (
        <EmptyState message='No data available' />
      )}
    </section>
  );

  return showTabs ? <div className='recommendations-main-container'>{chartContent}</div> : chartContent;
};

export default OpportunitiesChart;
